import React, { useEffect, useState, useCallback } from "react";
import Modal from "react-modal";
import Select from "react-select";
import {
  MdLocalShipping,
  MdPlace,
  MdPublic,
  MdClose,
  MdCheck,
} from "react-icons/md";
import apiEndpoints from "@store/apiEndpoints";
import errorFunctions from "@store/errorFunctions";
import "./AddressConfirmModal.css";

const COUNTRY_OPTIONS = [
  { value: "AU", label: "Australia" },
  { value: "NZ", label: "New Zealand" },
];

/**
 * Return address confirmation gate shown before a ticket is created for a
 * non-commercial (retailer) role. Confirms the address currently on the
 * customer's account, or lets staff correct it for this ticket only.
 *
 * Props:
 * - isOpen: boolean
 * - mode: "confirm" | "edit"
 * - editOnly: boolean - opened straight into the edit form (no confirm step),
 *   so the secondary button dismisses instead of going back
 * - address: { address_line_one, address_line_two, suburb, state, postcode, country }
 * - loading: boolean - address still being fetched
 * - submitting: boolean - ticket creation in flight
 * - protectedFetch: (url, method, body, resHandler) => Promise - for location lookups
 * - userInfo: logged-in staff user info - gates the Country select to Admins
 * - onConfirm: () => void
 * - onEdit: () => void
 * - onSaveEdit: (address) => void
 * - onBackToConfirm: () => void
 * - onClose: () => void
 */
const AddressConfirmModal = ({
  isOpen,
  mode,
  editOnly,
  address,
  loading,
  submitting,
  protectedFetch,
  userInfo,
  onConfirm,
  onEdit,
  onSaveEdit,
  onBackToConfirm,
  onClose,
}) => {
  const [draft, setDraft] = useState(address || {});
  const [stateOptions, setStateOptions] = useState([]);
  const [suburbOptions, setSuburbOptions] = useState([]);
  const [postcodeOptions, setPostcodeOptions] = useState([]);

  const isCountryEditable = ["Admin"].includes(userInfo?.user?.role);
  const isAu = (draft.country || "AU") === "AU";

  useEffect(() => {
    if (isOpen) {
      setDraft(address || {});
      setStateOptions([]);
      setSuburbOptions([]);
      setPostcodeOptions([]);
    }
  }, [isOpen, address]);

  const fetchStates = useCallback(
    (country) => {
      protectedFetch(
        apiEndpoints.location.states(country),
        "GET",
        null,
        errorFunctions.checkResponseThenJson,
      )
        .then((res) => {
          setStateOptions(
            (res || []).map((x) => ({ value: x.code, label: x.name })),
          );
        })
        .catch((err) => console.log("Error fetching states:", err));
    },
    [protectedFetch],
  );

  const fetchSuburbs = useCallback(
    (state, query) => {
      if (!state) return;
      protectedFetch(
        apiEndpoints.location.suburbs(state, "", query),
        "GET",
        null,
        errorFunctions.checkResponseThenJson,
      )
        .then((res) => {
          setSuburbOptions(
            (res?.results || []).map((x) => ({
              value: x.name,
              label: x.name,
            })),
          );
        })
        .catch((err) => console.log("Error fetching suburbs:", err));
    },
    [protectedFetch],
  );

  const fetchPostcodes = useCallback(
    (suburb, state) => {
      if (!suburb || !state) return;
      protectedFetch(
        apiEndpoints.location.postcodes(suburb, state),
        "GET",
        null,
        errorFunctions.checkResponseThenJson,
      )
        .then((res) => {
          setPostcodeOptions(
            (res?.results || []).map((x) => ({
              value: x.code,
              label: x.code,
            })),
          );
        })
        .catch((err) => console.log("Error fetching postcodes:", err));
    },
    [protectedFetch],
  );

  // Prefill the option lists with the draft's current values as soon as the
  // staff member opens the edit view, so the existing address is selectable.
  useEffect(() => {
    if (isOpen && mode === "edit" && (draft.country || "AU") === "AU") {
      fetchStates(draft.country || "AU");
      if (draft.state) fetchSuburbs(draft.state);
      if (draft.suburb && draft.state) {
        fetchPostcodes(draft.suburb, draft.state);
      }
    }
    // Only re-run when the edit view is (re)entered, not on every keystroke.
  }, [isOpen, mode]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setDraft((prev) => ({ ...prev, [id]: value }));
  };

  const handleCountryChange = (option) => {
    const country = option?.value || "AU";
    setDraft((prev) => ({
      ...prev,
      country,
      state: "",
      suburb: "",
      postcode: "",
    }));
    setStateOptions([]);
    setSuburbOptions([]);
    setPostcodeOptions([]);
    if (country === "AU") fetchStates(country);
  };

  const handleStateChange = (option) => {
    const state = option?.value || "";
    setDraft((prev) => ({ ...prev, state, suburb: "", postcode: "" }));
    setSuburbOptions([]);
    setPostcodeOptions([]);
    if (state) fetchSuburbs(state);
  };

  const handleSuburbChange = (option) => {
    const suburb = option?.value || "";
    setDraft((prev) => ({ ...prev, suburb, postcode: "" }));
    setPostcodeOptions([]);
    if (suburb && draft.state) fetchPostcodes(suburb, draft.state);
  };

  const handleSuburbInputChange = (val) => {
    if (val.length > 2) fetchSuburbs(draft.state, val);
  };

  const handlePostcodeChange = (option) => {
    setDraft((prev) => ({ ...prev, postcode: option?.value || "" }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    onSaveEdit(draft);
  };

  const countryValue = COUNTRY_OPTIONS.find(
    (x) => x.value === (draft.country || "AU"),
  );
  const stateValue =
    stateOptions.find((x) => x.value === draft.state) ||
    (draft.state ? { value: draft.state, label: draft.state } : null);
  const suburbValue =
    suburbOptions.find((x) => x.value === draft.suburb) ||
    (draft.suburb ? { value: draft.suburb, label: draft.suburb } : null);
  const postcodeValue =
    postcodeOptions.find((x) => x.value === draft.postcode) ||
    (draft.postcode ? { value: draft.postcode, label: draft.postcode } : null);

  const hasAddress =
    address?.address_line_one ||
    address?.address_line_two ||
    address?.suburb ||
    address?.state ||
    address?.postcode;

  return (
    <Modal
      isOpen={isOpen}
      contentLabel="Confirm Return address"
      className="input-modal addr-modal"
      style={{ overlay: { zIndex: 1002 } }}
      onRequestClose={submitting ? undefined : onClose}
    >
      <div className="card addr-card">
        {mode === "edit" ? (
          <form onSubmit={handleSave}>
            <div className="card-header">
              <MdPlace className="addr-header-icon" />
              <h6>Edit Return Address</h6>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="addr-modal-close"
                aria-label="Close"
              >
                <MdClose size={18} />
              </button>
            </div>
            <div className="card-body">
              <div className="addr-section-label">
                <MdPlace size={13} />
                Address
              </div>
              <div className="addr-group">
                <div className="addr-row">
                  <div className="addr-col">
                    <label htmlFor="address_line_one">Address Line 1</label>
                    <textarea
                      id="address_line_one"
                      className="form-control"
                      onChange={handleChange}
                      value={draft.address_line_one || ""}
                    />
                  </div>
                  <div className="addr-col">
                    <label htmlFor="address_line_two">Address Line 2</label>
                    <textarea
                      id="address_line_two"
                      className="form-control"
                      onChange={handleChange}
                      value={draft.address_line_two || ""}
                    />
                  </div>
                </div>
              </div>

              <div className="addr-section-label">
                <MdPublic size={13} />
                Location
              </div>
              <div className="addr-group">
                <div className="addr-row">
                  <div className="addr-col">
                    <label htmlFor="addr-country">Country</label>
                    <Select
                      inputId="addr-country"
                      classNamePrefix="react-select"
                      isDisabled={!isCountryEditable}
                      value={countryValue}
                      options={COUNTRY_OPTIONS}
                      onChange={handleCountryChange}
                    />
                  </div>
                  <div className="addr-col">
                    <label htmlFor="addr-state">State</label>
                    {isAu ? (
                      <Select
                        inputId="addr-state"
                        classNamePrefix="react-select"
                        options={stateOptions}
                        value={stateValue}
                        onChange={handleStateChange}
                      />
                    ) : (
                      <input
                        type="text"
                        id="state"
                        className="form-control"
                        onChange={handleChange}
                        value={draft.state || ""}
                      />
                    )}
                  </div>
                </div>
                <div className="addr-row">
                  <div className="addr-col">
                    <label htmlFor="addr-suburb">Suburb</label>
                    {isAu ? (
                      <Select
                        inputId="addr-suburb"
                        classNamePrefix="react-select"
                        options={suburbOptions}
                        value={suburbValue}
                        onChange={handleSuburbChange}
                        onInputChange={handleSuburbInputChange}
                      />
                    ) : (
                      <input
                        type="text"
                        id="suburb"
                        className="form-control"
                        onChange={handleChange}
                        value={draft.suburb || ""}
                      />
                    )}
                  </div>
                  <div className="addr-col">
                    <label htmlFor="addr-postcode">Postcode</label>
                    {isAu && postcodeOptions.length > 0 ? (
                      <Select
                        inputId="addr-postcode"
                        classNamePrefix="react-select"
                        options={postcodeOptions}
                        value={postcodeValue}
                        onChange={handlePostcodeChange}
                      />
                    ) : (
                      <input
                        type="text"
                        id="postcode"
                        className="form-control"
                        onChange={handleChange}
                        value={draft.postcode || ""}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="card-footer">
              <button
                type="button"
                className="btn btn-outline-secondary addr-btn-cancel"
                disabled={submitting}
                onClick={editOnly ? onClose : onBackToConfirm}
                data-testid="address-modal-back-btn"
              >
                {editOnly ? "Cancel" : "Back"}
              </button>
              <button
                type="submit"
                className="btn addr-btn-primary"
                disabled={submitting}
                data-testid="address-modal-save-btn"
              >
                {submitting ? (
                  <>
                    <span className="addr-spinner" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="card-header">
              <MdLocalShipping className="addr-header-icon" />
              <h6>Return Address</h6>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="addr-modal-close"
                aria-label="Close"
              >
                <MdClose size={18} />
              </button>
            </div>
            <div className="card-body">
              <div className="addr-intro">
                <p className="addr-intro-question">
                  Is this the correct return address?
                </p>
                <p className="addr-intro-note">
                  Once the device has completed service, it will be sent to
                  this address.
                </p>
              </div>

              <div className="addr-section-label">
                <MdPlace size={13} />
                Return Address
              </div>
              <div className="addr-group">
                {loading ? (
                  <div className="addr-loading">Loading address...</div>
                ) : hasAddress ? (
                  <div className="addr-readonly">
                    {address?.address_line_one && (
                      <div>{address.address_line_one}</div>
                    )}
                    {address?.address_line_two && (
                      <div>{address.address_line_two}</div>
                    )}
                    <div>
                      {[
                        address?.country,
                        address?.suburb,
                        address?.state,
                        address?.postcode,
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    </div>
                  </div>
                ) : (
                  <div className="addr-readonly-empty">
                    No return address on file
                  </div>
                )}
              </div>
            </div>
            <div className="card-footer">
              <button
                type="button"
                className="btn btn-outline-secondary addr-btn-cancel"
                disabled={loading || submitting}
                onClick={onEdit}
                data-testid="address-modal-edit-btn"
              >
                Edit
              </button>
              <button
                type="button"
                className="btn addr-btn-primary"
                disabled={loading || submitting}
                onClick={onConfirm}
                data-testid="address-modal-confirm-btn"
              >
                {submitting ? (
                  <>
                    <span className="addr-spinner" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <MdCheck size={16} />
                    Confirm
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default AddressConfirmModal;
